import * as React from "react";

import Cookies from "js-cookie";

import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src/index";

import initializeIntercomInstance from "./init";
import {
    ProviderValueProps,
    IntercomProviderProps,
    IntercomSettingsProperties
} from "./types";

const isWindowExist = typeof window !== "undefined";

export const IntercomContext = React.createContext<
    ProviderValueProps | undefined
>(undefined);

/**
 * IntercomProvider
 * exposes initIntercomInstance, boot, shutdown, show, hide methods to provider consumers
 * @param React.PropsWithChildren<IntercomProviderProps>
 * @returns
 */
export const IntercomProvider = ({
    appId,
    triggerBoot = false,
    children
}: React.PropsWithChildren<IntercomProviderProps>) => {
    const isBooted = React.useRef(false);
    const isInit = React.useRef(false);
    const stargateClient = useBxpWebStargateClient();

    const initIntercomInstance = React.useCallback(() => {
        if (!isInit.current) {
            initializeIntercomInstance(appId);
            isInit.current = true;
        }
    }, [appId]);

    // Ensure DOM is fully ready and stable before Intercom initialization
    const ensureDOMReady = React.useCallback(() => {
        return new Promise<void>((resolve) => {
            if (document.readyState === "complete") {
                // DOM is ready, but add buffer time for iframe stability
                setTimeout(resolve, 500);
            } else {
                // Wait for DOM completion, then add buffer time
                const cleanup = () => {
                    document.removeEventListener(
                        "DOMContentLoaded",
                        handleLoad
                    );
                    window.removeEventListener("load", handleLoad);
                };

                const handleLoad = () => {
                    cleanup();
                    clearTimeout(safetyTimeout);
                    setTimeout(resolve, 500);
                };

                // Add event listeners for DOM ready
                document.addEventListener("DOMContentLoaded", handleLoad);
                window.addEventListener("load", handleLoad);

                // Safety timeout: resolve after 10 seconds even if events don't fire
                const safetyTimeout = setTimeout(() => {
                    cleanup();
                    console.error(
                        "Intercom: DOM ready timeout reached, proceeding anyway"
                    );
                    resolve();
                }, 10000);
            }
        });
    }, []);

    const boot = React.useCallback(async () => {
        if (isWindowExist && isInit.current && !isBooted.current) {
            const intercomSettings = window.intercomSettings ?? {
                app_id: appId
            };

            try {
                // Wait for DOM to be fully ready and stable
                await ensureDOMReady();

                window.Intercom?.("boot", {
                    alignment: "right",
                    horizontal_padding: 20,
                    vertical_padding: 20,
                    custom_launcher_selector: "#intercomLauncher",
                    ...intercomSettings
                });
                window.Intercom?.("trackEvent", "widget-loaded");
            } catch (error) {
                console.warn("Intercom boot failed:", error);
            } finally {
                isBooted.current = true; // Set value as true to enable functionality of show/hide methods
            }
        }
    }, [appId, ensureDOMReady]);

    const shutdown = React.useCallback(() => {
        if (!isBooted.current) return;
        if (isWindowExist) {
            window.Intercom?.("shutdown");
            delete window.intercomSettings;
            isBooted.current = false;
        }
    }, []);

    const show = React.useCallback(() => {
        if (isWindowExist && isBooted.current) {
            window.Intercom?.("show");
        }
    }, []);

    const hide = React.useCallback(() => {
        if (isWindowExist && isBooted.current) {
            window.Intercom?.("hide");
        }
    }, []);

    React.useEffect(() => {
        if (isWindowExist && triggerBoot) {
            (async function () {
                const intercomSettingsObj: IntercomSettingsProperties = {
                    app_id: appId
                };

                const anonId = Cookies.get("ajs_anonymous_id") || "";
                if (anonId) {
                    intercomSettingsObj["anonymousId"] = anonId;
                }
                const getwayApiMeGetResponse = await stargateClient
                    .me({})
                    .catch(() => null);
                if (getwayApiMeGetResponse) {
                    intercomSettingsObj["name"] = getwayApiMeGetResponse.name;
                    intercomSettingsObj["email"] = getwayApiMeGetResponse.email;
                }

                if (isWindowExist)
                    window.intercomSettings = intercomSettingsObj;

                if (!isInit.current) initIntercomInstance();
                if (isInit.current && !isBooted.current) {
                    boot();
                }
            })();
        }

        // clean up code
        return () => {
            if (isInit.current) shutdown();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerBoot, stargateClient]);

    const providerValue: ProviderValueProps = React.useMemo(() => {
        return {
            initIntercomInstance,
            boot,
            shutdown,
            show,
            hide
        };
    }, [initIntercomInstance, boot, shutdown, show, hide]);

    return (
        <IntercomContext.Provider value={providerValue}>
            {children}
        </IntercomContext.Provider>
    );
};

export const useIntercomContext = () => {
    const context = React.useContext(IntercomContext);

    if (context === undefined) {
        throw new Error("This hook must be used within IntercomProvider");
    }

    return context;
};
